import React, { useRef } from 'react';
import { Upload, Loader2, Image as ImageIcon, X } from 'lucide-react';
import { usePhotoUpload } from '../hooks/usePhotoUpload';
import { useToast } from '../hooks/use-toast';

/**
 * Reusable photo uploader + URL input.
 *
 * Props:
 *   value       — current image URL (absolute)
 *   onChange    — (newUrl: string) => void
 *   testId      — base data-testid (button gets `${testId}-button`)
 *   label       — small label rendered above
 *   aspect      — 'square' | 'portrait' | 'wide' (preview shape)
 */
export default function PhotoUploader({ value, onChange, testId = 'photo', label = 'Photo', aspect = 'wide' }) {
  const inputRef = useRef(null);
  const { upload, uploading, progress } = usePhotoUpload();
  const { toast } = useToast();

  const aspectClass = { square: 'aspect-square', portrait: 'aspect-[3/4]', wide: 'aspect-[16/9]' }[aspect] || 'aspect-[16/9]';

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await upload(file);
      onChange(res.absoluteUrl);
      toast({ title: 'Photo uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || err.message });
    } finally {
      e.target.value = '';
    }
  };

  return (
    <div data-testid={`${testId}-uploader`}>
      <label className="text-xs text-[#6A6A6A] block mb-1.5">{label}</label>
      <div className={`relative w-full ${aspectClass} rounded-2xl overflow-hidden border border-[#EFE3D8] bg-[#FBF7F2] flex items-center justify-center group`}>
        {value ? (
          <>
            <img src={value} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remove photo"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <div className="text-center text-[#9A8A7E]">
            <ImageIcon className="w-7 h-7 mx-auto mb-1" />
            <p className="text-xs">No photo yet</p>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center text-[#7B2C3B] text-xs">
            <Loader2 className="w-5 h-5 animate-spin mb-1.5" />
            Uploading… {progress}%
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={onFile}
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          data-testid={`${testId}-button`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] disabled:opacity-60"
        >
          <Upload className="w-3.5 h-3.5" /> {value ? 'Replace' : 'Upload photo'}
        </button>
        <input
          type="text"
          data-testid={`${testId}-url-input`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="or paste a URL"
          className="flex-1 px-3 py-2 rounded-full border border-[#EFE3D8] bg-white text-xs focus:outline-none focus:border-[#7B2C3B]"
        />
      </div>
      <p className="text-[10px] text-[#9A8A7E] mt-1">JPG, PNG, WEBP, or GIF · up to 8 MB</p>
    </div>
  );
}
